/**
 * UPS-specific claim info collected from the merchant at claim creation
 * time. Includes dropdown enums + zod schemas for everything the UPS batch
 * claim xlsx needs in its damage section (columns Q–AJ).
 *
 * Single source of truth for:
 *   - UI dropdowns (render from the enum values)
 *   - Zod schemas stored on ShippingClaim.carrierSpecificInfo.ups
 *   - xlsx file writer (enum values are emitted verbatim into the sheet)
 *
 * Yes/No toggles are `z.boolean()` at the model layer; the xlsx writer
 * translates to "Yes"/"No" at serialization. Template column R ("Will
 * photos be provided?") is intentionally omitted — it's computed from
 * whether the claim has photos attached.
 */
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";

// ---------------------------------------------------------------------------
// Dropdown enums (labels must match the UPS template Hidden Sheet exactly)
// ---------------------------------------------------------------------------

/** Column X — Current Location of the Damaged Package */
export enum UpsDamageLocation {
  AT_SHIPPER = "At Shipper's Location",
  AT_CONSIGNEE = "At Consignee's Location",
  AT_OTHER = "At Other Location",
}
export const UpsDamageLocationSchema = createEnum(UpsDamageLocation, {
  enumName: "UpsDamageLocation",
});

/** Column AC — Condition of Damaged Item(s) */
export enum UpsItemCondition {
  BENT = "Bent",
  CRACKED = "Cracked",
  CRUSHED = "Crushed",
  DENTED = "Dented",
  PUNCTURE = "Puncture",
  SCRATCHED = "Scratched",
  SHATTERED = "Shattered",
  SOILED = "Soiled",
  SPOILED = "Spoiled",
  TORN = "Torn",
  WET = "Wet",
  OTHER = "Other",
}
export const UpsItemConditionSchema = createEnum(UpsItemCondition, {
  enumName: "UpsItemCondition",
});

/** Column AE — Package Type */
export enum UpsPackageType {
  BOX = "Box",
  BAG = "Bag",
  CASE = "Case",
  ENVELOPE = "Envelope",
  PAIL = "Pail/Bucket/Can",
  WRAP = "Plastic or Paper-Wrap",
  TUBE = "Tube",
  OTHER = "Other",
}
export const UpsPackageTypeSchema = createEnum(UpsPackageType, {
  enumName: "UpsPackageType",
});

/** Column AH — How was Package Sealed? */
export enum UpsSealMethod {
  CABLE = "Cable",
  CAP = "Cap",
  CLASP = "Clasp",
  GLUE = "Glue",
  HINGE = "Hinge",
  LATCH = "Latch",
  NAILS = "Nails",
  NONE = "None",
  STAPLES = "Staples",
  STITCH = "Stitch",
  STRAPS = "Straps",
  STRING = "String",
  TAPE = "Tape",
  TIES = "Ties",
  TWIST = "Twist",
  ZIPPER = "Zipper",
}
export const UpsSealMethodSchema = createEnum(UpsSealMethod, {
  enumName: "UpsSealMethod",
});

/**
 * Column AI — Packaging Materials.
 * UPS spells these as camel-ish tokens ("AirBags", "DoubleBoxed",
 * "LooseFill") verbatim from the Hidden Sheet — do not prettify or UPS
 * rejects the row.
 */
export enum UpsPackagingMaterial {
  AIR_BAGS = "AirBags",
  WRAP = "Wrap",
  CORRUGATED = "Corrugated",
  DOUBLE_BOXED = "DoubleBoxed",
  FOAM = "Foam",
  LOOSE_FILL = "LooseFill",
  PAPER = "Paper",
  PEANUTS = "Peanuts",
  NONE = "None",
  OTHER = "Other",
}
export const UpsPackagingMaterialSchema = createEnum(UpsPackagingMaterial, {
  enumName: "UpsPackagingMaterial",
});

// ---------------------------------------------------------------------------
// Damage section inputs (columns Q–AJ)
// ---------------------------------------------------------------------------

export const UpsDamageInfoSchema = z.object({
  /** Q — Both contents and packaging available for inspection? */
  contentsAndPackagingAvailable: z.boolean(),
  /** S — Date damage was discovered (not in the future) */
  dateDamageDiscovered: z.date(),
  /** T — Total items in the package (≤9999) */
  totalItemsInPackage: z.number().int().min(1).max(9999),
  /** U — Are items missing from the package? */
  itemsMissing: z.boolean(),
  /** V — Contains dangerous goods? */
  containsDangerousGoods: z.boolean(),
  /** W — Can package be sealed and transported safely? */
  canBeSealedSafely: z.boolean(),
  /** X — Current location of the damaged package */
  currentLocation: UpsDamageLocationSchema,
  /** Y — Contact name at "Other Location" (conditional on X, ≤35) */
  otherLocationContactName: z.string().nullish(),
  /** Z — City at "Other Location" (conditional, ≤50) */
  otherLocationCity: z.string().nullish(),
  /** AA — Postal code at "Other Location" (conditional, ≤8) */
  otherLocationPostal: z.string().nullish(),
  /** AB — Country code at "Other Location" (conditional) */
  otherLocationCountry: z.string().nullish(),
  /** AC — Condition of damaged item(s) */
  itemCondition: UpsItemConditionSchema,
  /** AD — "Other" condition description (conditional on itemCondition=OTHER, ≤50) */
  itemConditionOther: z.string().nullish(),
  /** AE — Package type */
  packageType: UpsPackageTypeSchema,
  /** AF — "Other" package type (conditional on packageType=OTHER, ≤50) */
  packageTypeOther: z.string().nullish(),
  /** AG — Damage to outside of package? */
  damageToOutside: z.boolean(),
  /** AH — How was package sealed? */
  howPackageSealed: UpsSealMethodSchema,
  /** AI — Packaging materials */
  packagingMaterials: UpsPackagingMaterialSchema,
  /** AJ — "Other" packaging materials (conditional, ≤50) */
  packagingMaterialsOther: z.string().nullish(),
});
export type UpsDamageInfo = z.infer<typeof UpsDamageInfoSchema>;

/**
 * UPS-specific claim info. Currently only carries the damage section; more
 * UPS fields may be added (e.g. whether the merchant has already filed
 * directly with UPS, InsureShield policy number if covered, etc.).
 */
export const UpsClaimInfoSchema = z.object({
  damage: UpsDamageInfoSchema.nullish(),
});
export type UpsClaimInfo = z.infer<typeof UpsClaimInfoSchema>;
